import { useMemo, useState, useEffect } from 'preact/hooks'
import { postBeaconTracking } from 'libs/searchspring/searchspring-tracking'
import { getImages, redirect } from 'libs/searchspring/searchspring-utils'
import { getBadges, addBadgeSale } from 'libs/shopify'
import Image from 'snippets/image/image'
import PlpCardPrice from 'snippets/plp-card-price/plp-card-price'
import PlpSwatches from 'modules/plp-swatches/plp-swatches'
import CarouselSwiper from 'modules/carousel-swiper/carousel-swiper'
import Badge from 'snippets/badge/badge'
import { handleize } from '../../helpers/dom'
import { openModal } from 'helpers/dom'
import {
  parseStockJson,
  resolveActiveWarehouse,
  getMetafieldKey,
  isPreorderStatus,
  STORAGE_1P_KEY,
  STORAGE_2P_KEY,
  isSellable
} from 'helpers/stock-status'

function PlpCard({
  id,
  isLastItem = false,
  title,
  subtitle = '',
  handle,
  imageUrl,
  isCarousel = false,
  images = [],
  isShowBadge = false,
  price,
  originalPrice,
  collectionHandle,
  tags = [],
  className = 'relative flex flex-col p-4 border-b md:!border border-default lg:relative lg:overflow-hidden plp-card',
  ratioImage = 'aspect-[1/1] overflow-hidden',
  linkedColors = [],
  currentSize = '',
  variants = [],
  initVariant,
  video,
  sku,
  showSwatches = true,
  showPrice = true,
  intellisuggestData,
  intellisuggestSignature,
  collectionClickTracking,
  beaconClickTracking,
  showCallout = true,
  isBundleProduct = false,
  quickView,
  collectionName = [],
  onCurrentProductChange,
  isCartRelated = false,
  brisbaneStockJson = null,
  melbourneStockJson = null
}) {
  const [currentHandle, setCurrentHandle] = useState(handle)
  const [currentVariantId, setCurrentVariantId] = useState(variants[0]?.id)
  const [currentProductId, setcurrentProductId] = useState(variants[0]?.product_id)
  const [currentTitle, setCurrentTitle] = useState(title)
  const [currentSubTitle, setCurrentSubTitle] = useState(subtitle)
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl)
  const [currentPrice, setCurrentPrice] = useState(price)
  const [currentSku, setCurrentSku] = useState(sku)
  const [currentVideo, setCurrentVideo] = useState(video)
  const [currentCompareAtPrice, setCurrentCompareAtPrice] = useState(originalPrice)
  const [currentListImage, setCurrentListImage] = useState(images)
  const [carouselInstance, setCarouselInstance] = useState(null)
  const [currentVariant, setInitVariant] = useState(initVariant)
  const [currentTags, setCurrentTags] = useState(tags)
  const [gmStateReady, setGmStateReady] = useState(false)
  const [warehouse1P, setWarehouse1P] = useState(() => {
    try { return localStorage.getItem(STORAGE_1P_KEY) || null } catch { return null }
  })
  const [warehouse2P, setWarehouse2P] = useState(() => {
    try { return localStorage.getItem(STORAGE_2P_KEY) || null } catch { return null }
  })

  useEffect(() => {
    const onPostcodeUpdated = (e) => {
      setWarehouse1P(e.detail.warehouse1P ?? null)
      setWarehouse2P(e.detail.warehouse2P ?? null)
    }
    window.addEventListener('postcode:updated', onPostcodeUpdated)
    return () => window.removeEventListener('postcode:updated', onPostcodeUpdated)
  }, [])

  const productUrl = useMemo(() => `/products/${currentHandle}`, [currentHandle])
  const productCardCalloutConfig = window.productCardCalloutConfig

  // Wait for GM_STATE to be ready before calculating badges
  useEffect(() => {
    if (window.GM_STATE?.customs?.badge?.items) {
      setGmStateReady(true)
    } else if (window.GM_STATE?.onReady) {
      window.GM_STATE.onReady(() => {
        setGmStateReady(true)
      })
    } else {
      // Fallback: check periodically if GM_STATE not available
      const checkInterval = setInterval(() => {
        if (window.GM_STATE?.customs?.badge?.items) {
          setGmStateReady(true)
          clearInterval(checkInterval)
        }
      }, 100)

      // Clear interval after 5 seconds to prevent memory leak
      setTimeout(() => {
        clearInterval(checkInterval)
      }, 5000)

      return () => clearInterval(checkInterval)
    }
  }, [currentHandle])

  // Calculate badges with memoization, depends on GM_STATE readiness
  const badges = useMemo(() => {
    let badgeList = getBadges(currentTags)
    badgeList = addBadgeSale(badgeList, parseFloat(currentPrice), parseFloat(currentCompareAtPrice))

    // Stock badge from warehouse JSON (SearchSpring fields)
    if (brisbaneStockJson || melbourneStockJson) {
      // Strip tag-based stock badges — warehouse data has full authority when present
      const STOCK_BADGE_TAGS = ['label-rts', 'label-preorder', 'label-arriving-soon']
      badgeList = badgeList.filter(b => !STOCK_BADGE_TAGS.includes(b.tag))

      let stockBadge = null
      const hasWarehouse = warehouse1P !== null || warehouse2P !== null

      if (hasWarehouse) {
        // Postcode resolved — use active warehouse
        const variantLike = {
          metafields: {
            brisbane_stock_json: brisbaneStockJson,
            melbourne_stock_json: melbourneStockJson
          }
        }
        const activeWarehouse = resolveActiveWarehouse(warehouse1P, warehouse2P, variantLike)
        const metafieldKey = activeWarehouse ? getMetafieldKey(activeWarehouse) : null
        const rawStock = metafieldKey ? variantLike.metafields[metafieldKey] : null
        const stockData = rawStock ? parseStockJson(rawStock) : null

        if (stockData?.stockstatusmessage === 'instock' && isSellable(stockData)) {
          stockBadge = { text: 'Ready to Ship', tag: 'stock-ready-to-ship', customClass: 'badge-label-rts', backgroundColor: '', textColor: '' }
        } else if (isPreorderStatus(stockData?.stockstatusmessage) && isSellable(stockData)) {
          stockBadge = { text: 'Pre-order', tag: 'stock-preorder', customClass: 'badge-label-preorder', backgroundColor: '', textColor: '' }
        }
      } else {
        // No postcode — badge only if both warehouses agree
        const brisbaneData = brisbaneStockJson ? parseStockJson(brisbaneStockJson) : null
        const melbourneData = melbourneStockJson ? parseStockJson(melbourneStockJson) : null
        const bStatus = brisbaneData?.stockstatusmessage
        const mStatus = melbourneData?.stockstatusmessage

        if (bStatus === 'instock' && mStatus === 'instock' && isSellable(brisbaneData) && isSellable(melbourneData)) {
          stockBadge = { text: 'Ready to Ship', tag: 'stock-ready-to-ship', customClass: 'badge-label-rts', backgroundColor: '', textColor: '' }
        } else if (isPreorderStatus(bStatus) && isPreorderStatus(mStatus) && isSellable(brisbaneData) && isSellable(melbourneData)) {
          stockBadge = { text: 'Pre-order', tag: 'stock-preorder', customClass: 'badge-label-preorder', backgroundColor: '', textColor: '' }
        }
      }

      if (stockBadge) badgeList.push(stockBadge)
    }

    return badgeList
  }, [currentTags, currentPrice, currentCompareAtPrice, gmStateReady, currentHandle, warehouse1P, warehouse2P, brisbaneStockJson, melbourneStockJson])

  console.log('Calculated badges:', currentHandle, badges)
  const setCurrentProduct = (handle, title, subtitle, imageUrl, price, compareAtPrice, images, video, tags, sku, swatchName, variants, bundleChilds, productId, id) => {
    setCurrentHandle(handle)
    setCurrentTitle(title)
    setCurrentSubTitle(subtitle)
    setCurrentImageUrl(imageUrl)
    setCurrentPrice(price)
    setCurrentCompareAtPrice(compareAtPrice)
    setCurrentListImage(images)
    setCurrentVideo(video)
    setCurrentTags(tags)
    const nextVariantId = variants[0] ? variants[0].id.replace('gid://shopify/ProductVariant/', '') : ''
    const nextProductId = productId ? productId.replace('gid://shopify/Product/', '') : ''
    setCurrentVariantId(nextVariantId)
    setcurrentProductId(nextProductId)
    setCurrentSku(sku)

    if (id) {
      const newVariant = {
        id: parseInt(id)
      }
      setInitVariant(newVariant)
    }

    if (typeof onCurrentProductChange === 'function') {
      onCurrentProductChange({ rootId: id && typeof id !== 'undefined' ? id : null, productId: nextProductId, variantId: nextVariantId, price })
    }
  }

  const CarouselOptions = {
    slidesPerView: 1,
    freeMode: {
      enabled: true,
      sticky: true
    },
    grabCursor: true,
    speed: 500,
    pagination: {
      clickable: true
    }
  }

  const onTracking = async (e) => {
    e.preventDefault()
    if (intellisuggestData && intellisuggestSignature && collectionClickTracking) {
      collectionClickTracking(productUrl, intellisuggestData, intellisuggestSignature)
      redirect(`${productUrl}`, '', e.ctrlKey)
    } else if (beaconClickTracking) {
      try {
        const result = await postBeaconTracking(beaconClickTracking)
        if (result) {
          redirect(`${productUrl}`, '', e.ctrlKey)
        }
      } finally {
        redirect(`${productUrl}`, '', e.ctrlKey)
      }
    } else {
      redirect(`${productUrl}`, '', e.ctrlKey)
    }
  }

  const callOut = useMemo(() => {
    if (!showCallout || !productCardCalloutConfig || productCardCalloutConfig?.length <= 0 || !productCardCalloutConfig[0].enable) {
      return <></>
    }

    const isMetafield = productCardCalloutConfig.some((item) => item.isMetafield)
    const callOutConfigs = productCardCalloutConfig.sort((a, b) => a.priority - b.priority)
    const firstCallOutCollection = isMetafield
      ? callOutConfigs?.find((item) => item.collections?.some((name) => collectionName.some(item => handleize(item) === handleize(name))))
      : callOutConfigs[0]

    return (firstCallOutCollection && firstCallOutCollection.text &&
      <p className="text-xs font-medium promotion-text" style={firstCallOutCollection.color ? `color: ${firstCallOutCollection.color};` : ''}>
        {firstCallOutCollection.text}
      </p>
    )
  }, [currentHandle])

  const handleBundleVariantClick = (variant, imageUrl, title) => {
    if (!variant?.id) return
    const checkbox = document.querySelector(`[data-variant-id="${variant.id}"]`)
    if (!checkbox) return

    const bundleIndex = checkbox.closest('[data-accordion-bundle]')?.getAttribute('data-accordion-bundle') || 1

    const isSelected = checkbox.classList.contains('checked')
    if (isSelected) {
      checkbox.classList.remove('checked')
      removeBundleItem(variant.id, bundleIndex)
    } else {
      const accordionBundle = checkbox.closest('[data-accordion-bundle]')
      if (accordionBundle) {
        const previouslyChecked = accordionBundle.querySelector('.plp-card-checkbox.checked')
        if (previouslyChecked) {
          const previousVariantId = previouslyChecked.getAttribute('data-variant-id')
          const previousBundleIndex = accordionBundle.getAttribute('data-accordion-bundle') || 1
          previouslyChecked.classList.remove('checked')
          removeBundleItem(previousVariantId, previousBundleIndex)
        }
      }
      checkbox.classList.add('checked')
      addBundleItem(variant, imageUrl, title, bundleIndex)
    }
  }

  const addBundleItem = (variant, imageUrl, title, bundleIndex) => {
    const lineItemsContainer = document.querySelector('.product-bundles__line-items')
    if (!lineItemsContainer) return

    const uniqueId = `${variant.id}-bundle-${bundleIndex}`
    const existingItem = lineItemsContainer.querySelector(`[data-unique-id="${uniqueId}"]`)
    if (existingItem) return

    const newItem = document.createElement('li')
    newItem.className = 'product-bundles__line-item'
    newItem.setAttribute('data-variant-id', variant.id)
    newItem.setAttribute('data-bundle-index', bundleIndex)
    newItem.setAttribute('data-unique-id', uniqueId)
    newItem.setAttribute('data-quantity', '1')

    newItem.innerHTML = `
      <img src="${imageUrl}" alt="${title}">
      <div class="product-bundles__line-item-quantity">
        <div class="quantity-input-wrapper">
          <button type="button" class="quantity-btn minus" data-action="decrease" data-variant-id="${variant.id}">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 16 16">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M3 8h10"/>
            </svg>
          </button>
          <input type="number" class="quantity-input" value="1" min="1" max="5" data-variant-id="${variant.id}" />
          <button type="button" class="quantity-btn plus" data-action="increase" data-variant-id="${variant.id}">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 16 16">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8 3v10M3 8h10"/>
            </svg>
          </button>
        </div>
      </div>
      <button class="product-bundles__line-item-remove" data-remove-item data-variant-id="${variant.id}">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none"><path fill="#fff" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z"/><path fill="#363636" stroke="#363636" stroke-linecap="round" stroke-linejoin="round" d="m9.594 14.406 4.812-4.812"/><path fill="#363636" stroke="#363636" stroke-linecap="round" stroke-linejoin="round" d="m9.594 9.594 4.812 4.812"/></svg>
      </button>
    `

    const quantityInput = newItem.querySelector('.quantity-input')
    const minusBtn = newItem.querySelector('[data-action="decrease"]')
    const plusBtn = newItem.querySelector('[data-action="increase"]')

    const updateButtonStates = () => {
      const currentQuantity = parseInt(quantityInput.value)
      minusBtn.disabled = currentQuantity <= 1
      plusBtn.disabled = currentQuantity >= 5
    }

    updateButtonStates()

    minusBtn.addEventListener('click', () => {
      const currentQuantity = parseInt(quantityInput.value)
      if (currentQuantity > 1) {
        quantityInput.value = currentQuantity - 1
        newItem.setAttribute('data-quantity', quantityInput.value)
        updateButtonStates()
        updateBundleSummary()
        updateBundleTotal()
      }
    })

    plusBtn.addEventListener('click', () => {
      const currentQuantity = parseInt(quantityInput.value)
      if (currentQuantity < 5) {
        quantityInput.value = currentQuantity + 1
        newItem.setAttribute('data-quantity', quantityInput.value)
        updateButtonStates()
        updateBundleSummary()
        updateBundleTotal()
      }
    })

    quantityInput.addEventListener('change', () => {
      let value = parseInt(quantityInput.value)
      if (isNaN(value) || value < 1) {
        value = 1
      } else if (value > 5) {
        value = 5
      }
      quantityInput.value = value
      newItem.setAttribute('data-quantity', value)
      updateButtonStates()
      updateBundleSummary()
      updateBundleTotal()
    })

    const removeBtn = newItem.querySelector('[data-remove-item]')
    removeBtn.addEventListener('click', () => {
      removeBundleItem(variant.id, bundleIndex)
      const checkbox = document.querySelector(`[data-variant-id="${variant.id}"]`)
      if (checkbox) {
        checkbox.classList.remove('checked')
      }
    })

    lineItemsContainer.appendChild(newItem)
    updateBundleSummary()
    updateBundleTotal()
  }

  const updateBundleTotal = () => {
    const mainProductElement = document.querySelector('.product-bundles__line-items .main-product')
    const mainProductPrice = mainProductElement ? parseFloat(mainProductElement.getAttribute('data-main-product-price') || 0) : 0
    const mainProductComparePrice = mainProductElement ? parseFloat(mainProductElement.getAttribute('data-main-product-compare-price') || 0) : 0
    const bundleLineItems = document.querySelectorAll('.product-bundles__line-items .product-bundles__line-item:not(.main-product)')

    let totalBundlePrice = mainProductPrice
    let totalComparePrice = mainProductPrice

    const findProductInBundles = (variantId) => {
      let foundProduct = null
      let bundleIndex = 1

      while (window[`accordionCarouselProducts${bundleIndex}`]) {
        foundProduct = window[`accordionCarouselProducts${bundleIndex}`].find(p =>
          p.initVariant?.id === parseInt(variantId) ||
          p.variants?.some(v => v.id === parseInt(variantId))
        )
        if (foundProduct) break
        bundleIndex++
      }

      return foundProduct
    }

    bundleLineItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id')
      const quantity = parseInt(item.getAttribute('data-quantity') || 1)
      if (variantId) {
        const foundProduct = findProductInBundles(variantId)

        if (foundProduct) {
          const itemPrice = foundProduct.price || 0
          const itemComparePrice = foundProduct.compare_at_price_min || itemPrice
          totalBundlePrice += (itemPrice * quantity)
          totalComparePrice += (itemComparePrice * quantity)
        }
      }
    })

    const bundleContainer = document.querySelector('.product-bundles')
    const discountPercentage = bundleContainer ? parseFloat(bundleContainer.getAttribute('data-bundle-discount') || 0) : 0

    let finalPrice = totalBundlePrice
    let finalComparePrice = totalComparePrice

    if (bundleLineItems.length > 0 && discountPercentage > 0) {
      const discountAmount = (totalBundlePrice * discountPercentage) / 100
      finalPrice = totalBundlePrice - discountAmount
    } else {
      finalPrice = totalBundlePrice
      if (bundleLineItems.length === 0) {
        finalComparePrice = mainProductComparePrice > 0 ? mainProductComparePrice : totalBundlePrice
      } else {
        finalComparePrice = totalComparePrice
      }
    }

    const regularPriceElement = document.querySelector('[data-regular-price]')
    const comparePriceElement = document.querySelector('[data-compare-price]')

    if (regularPriceElement) {
      regularPriceElement.textContent = formatPrice(finalPrice)
    }
    if (comparePriceElement) {
      comparePriceElement.innerHTML = `
        <span class="product-bundles__original-price">${formatPrice(finalComparePrice)}</span>
      `
    }
    if (finalPrice === finalComparePrice) {
      comparePriceElement.innerHTML = '<span class="product-bundles__original-price"></span>'
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100)
  }

  const removeBundleItem = (variantId, bundleIndex) => {
    const lineItemsContainer = document.querySelector('.product-bundles__line-items')
    if (!lineItemsContainer) return
    const uniqueId = `${variantId}-bundle-${bundleIndex}`
    const itemToRemove = lineItemsContainer.querySelector(`[data-unique-id="${uniqueId}"]`)
    if (itemToRemove) {
      itemToRemove.remove()
      updateBundleSummary()
      updateBundleTotal()
    }
  }

  const updateBundleSummary = () => {
    const lineItems = document.querySelectorAll('.product-bundles__line-items .product-bundles__line-item')
    let totalItemCount = 0

    lineItems.forEach(item => {
      if (item.classList.contains('main-product')) {
        totalItemCount += 1
      } else {
        const quantity = parseInt(item.getAttribute('data-quantity') || 1)
        totalItemCount += quantity
      }
    })

    const itemCountElement = document.querySelector('[data-item-count]')
    if (itemCountElement) {
      itemCountElement.textContent = totalItemCount
    }
    if (window.updateBundleAccordionStates) {
      window.updateBundleAccordionStates()
    }

    if (window.updateBundleAddToCartButtonState) {
      window.updateBundleAddToCartButtonState()
    }
  }

  useEffect(() => {
    window.updateBundleSummary = updateBundleSummary
  }, [])

  return (
    <div className={`js-plp-card ${className} ${tags.includes('label-bundle-save') ? 'bundle-product' : ''}`}>
      <div className="relative mb-4 overflow-hidden plp-card-image">
        {isBundleProduct === true && (
          <div
            className="cursor-pointer plp-card-image-bundle"
            data-bundle-variant-id={currentVariant.id}
            onClick={() => handleBundleVariantClick(currentVariant, currentImageUrl, currentTitle)}
          >
            <div
              className="cursor-pointer plp-card-checkbox"
              data-variant-id={currentVariant.id}
            >
              <svg className="icon-check" xmlns="http://www.w3.org/2000/svg" width="10" height="7" fill="none"><path stroke="#363636" stroke-linecap="round" stroke-linejoin="round" d="M9.125.75 3.622 6 .875 3.375"/></svg>
              <svg className="icon-plus" xmlns="http://www/w3.org/2000/svg" width="14" height="14" fill="none"><path stroke="#363636" stroke-linecap="round" stroke-linejoin="round" d="M2.188 7h9.624M7 2.188v9.624"/></svg>
            </div>
            <Image
              imageUrl={[currentImageUrl]}
              alt={currentTitle}
              title={title}
              className={`${ratioImage} ${carouselInstance?.initialized ? 'hidden' : ''}`}
              imageClass="w-full h-full transition-all duration-300 ease-in-out hover:zoom"
              imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
              imageSrcSet={[200, 400, 600, 768]}
              isCover={true}
              isLazy={true}
              key={currentHandle}
            />
          </div>
        )}
        {isBundleProduct === false && (
          <>
            <button
              aria-label="Add to Wishlist"
              data-with-epi="true"
              class={`swym-button swym-add-to-wishlist-view-product product_${currentProductId} custom-swym-button text-[0]`}
              data-swaction="addToWishlist"
              data-product-id={currentProductId}
              data-variant-id={currentVariantId}
              data-product-url={`${window.location.origin}/products/${currentHandle}`}
              key={currentHandle}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                  <path d="M3.47387 11.7915C2.03576 7.98872 4.01085 4.48375 7.5094 4.03899C10.6995 3.63343 11.9384 6.52282 11.9916 6.65061C11.9931 6.65432 11.996 6.65498 12 6.65498C12.004 6.65498 12.0069 6.65432 12.0084 6.65061C12.0616 6.52282 13.3005 3.63343 16.4906 4.03899C19.9891 4.48375 21.9642 7.98872 20.5261 11.7915C18.9522 15.9534 12.1309 19.9279 12.0083 19.999C12.0046 20.0012 12.0044 20 12 20C11.9956 20 11.9954 20.0012 11.9917 19.999C11.8691 19.9279 5.04782 15.9534 3.47387 11.7915Z" stroke="#181818" stroke-width="1.6"/>
                </svg>
            </button>
            <a
              href={productUrl}
              title={title}
              className={`${isCarousel ? 'pointer-events-auto' : ''}`}
              onClick={onTracking}
            >
              {isCarousel ? (
                <>
                  <CarouselSwiper
                    key={currentHandle}
                    items={<ImageItems images={currentListImage} video={currentVideo} title={title} />}
                    className={`carousel-swiper-product-card overflow-hidden ${carouselInstance?.initialized ? '' : 'hidden'}`}
                    options={CarouselOptions}
                    pagination={true}
                    scrollbar={false}
                    navigation={true}
                    setCarouselInstance={setCarouselInstance}
                  />
                  <Image
                    imageUrl={[currentImageUrl]}
                    alt={currentTitle}
                    title={title}
                    className={`${ratioImage} ${carouselInstance?.initialized ? 'hidden' : ''}`}
                    imageClass="w-full h-full transition-all duration-300 ease-in-out hover:zoom"
                    imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
                    imageSrcSet={[200, 400, 600, 768]}
                    isCover={true}
                    isLazy={true}
                    key={currentHandle}
                  />
                </>
              ) : (
                <Image
                  imageUrl={[currentImageUrl]}
                  alt={currentTitle}
                  title={title}
                  className={ratioImage}
                  imageClass="w-full h-full transition-all duration-300 ease-in-out hover:zoom"
                  imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
                  imageSrcSet={[200, 400, 600, 768]}
                  isCover={true}
                  isLazy={true}
                  key={currentHandle}
                />
              )}
            </a>
          </>
        )}
        {isShowBadge && badges.length > 0 && (
          <div className="absolute z-10 flex flex-col gap-2 plp-card-badge-wrapper left-2 top-2 lg:left-4 lg:top-4">
            <Badge items={badges} className="flex flex-col gap-2 plp-card-badge" />
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 plp-card-info">
        <div className="flex items-center mb-2 plp-card-swatches-wrapper">
          {showSwatches ? (
            <PlpSwatches
              currentHandle={currentHandle}
              currentVariant={currentVariant}
              setCurrentProduct={setCurrentProduct}
              linkedColors={linkedColors}
              currentSize={currentSize}
              currentSku={sku}
              isLastItem={isLastItem}
              className={'flex-1'}
            />
          ) : (
            <></>
          )}
          {quickView && (
            <button className="hidden gap-2 px-4 py-2 text-xs font-medium rounded-full plp-card-quick-view-link lg:flex text-info-content bg-info-bg"
              onClick={(e) => {
                e.preventDefault()
                openModal(`quick-add-modal-${handle}`, currentSku)
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" width="16" height="16" viewBox="0 0 16 16"><g stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" clip-path="url(#clip0_2397_63446)"><path d="M8 3.5C3 3.5 1 8 1 8s2 4.5 7 4.5S15 8 15 8s-2-4.5-7-4.5Z"/><path d="M8 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/></g><defs><clipPath id="clip0_2397_63446"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>
              Quick View
            </button>
          )}
        </div>
        <div className="flex-1 plp-card-wrapper">
          <div className="plp-card-title js-plp-card-info mb-1.5 flex flex-1 flex-col">

            <a
              href={productUrl}
              title={currentTitle}
              className="flex-1 text-sm break-words plp-card-title-link text-grey-1000"
              onClick={onTracking}
            >
              {currentTitle}
            </a>
            <div className="flex-1 text-sm text-grey-500">{currentSubTitle}</div>
          </div>
          {showPrice ? (
            <div className="mt-auto plp-card-price-wrapper">
              <PlpCardPrice price={Number(currentPrice)} originalPrice={Number(currentCompareAtPrice)} isCartRelated={isCartRelated} />
            </div>
          ) : (
            <></>
          )}
          {callOut}
        </div>
      </div>
    </div>
  )
}

function ImageItems({ images, title, video }) {
  return images ? (
    images.map(
      (image, index) =>
        image && index < 5 && (
          video && index === 1 ? (
            <swiper-slide className="w-full" key={index}>
              <video
                className="aspect-[1/1] overflow-hidden"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                loading={index === 0 ? 'eager' : 'lazy'}
              >
                <source src={video} type="video/mp4" />
              </video>
            </swiper-slide>
          ) : (
          <swiper-slide className="w-full" key={index}>
            <Image
              imageUrl={getImages(image)}
              className="aspect-[1/1] overflow-hidden"
              imageClass="w-full h-full transition-all duration-300 ease-in-out hover:zoom"
              imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
              isCover={true}
              isLazy={true}
              alt={title}
              loading={index === 0 ? 'eager' : 'lazy'}
              imageSrcSet={[200, 400, 600, 768]}
            />
          </swiper-slide>
          )
        )
    )
  ) : (
    <Image
      className="aspect-[1/1] w-full overflow-hidden"
      imageClass="w-full h-full transition-all duration-300 ease-in-out hover:zoom"
      imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
      isCover={true}
      isLazy={true}
      alt={title}
    />
  )
}

export default PlpCard
